import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";

interface DataTableShellProps extends React.ComponentProps<"div"> {
  /** Height of the scroll area. Defaults to `calc(100vh - 330px)`. */
  height?: string;
  /** Minimum height. Defaults to 400px. */
  minHeight?: string;
  /** Footer content (e.g. pagination, "load more"). Rendered below the scroll area. */
  footer?: React.ReactNode;
}

/**
 * Reusable card shell for data tables.
 *
 * Provides: rounded card wrapper, thin-scrollbar scroll container,
 * sticky-header support (add `sticky top-0 bg-card z-10` to your `<thead>`),
 * and an optional footer outside the scroll area.
 *
 * Usage:
 * ```tsx
 * <DataTableShell footer={<Pagination />}>
 *   <Table className="table-fixed">
 *     <TableHeader className="sticky top-0 bg-card z-10 shadow-[0_1px_0_var(--color-border)]">
 *       ...
 *     </TableHeader>
 *     <TableBody>...</TableBody>
 *   </Table>
 * </DataTableShell>
 * ```
 */
export const DataTableShell = forwardRef<HTMLDivElement, DataTableShellProps>(
  (
    {
      height = "calc(100vh - 330px)",
      minHeight = "400px",
      footer,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn("rounded-xl bg-card border border-border overflow-hidden", className)} {...props}>
        <div
          ref={ref}
          className="overflow-auto [scrollbar-width:thin] [scrollbar-color:var(--color-border)_transparent]"
          style={{ height, minHeight }}
        >
          {children}
        </div>
        {footer && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            {footer}
          </div>
        )}
      </div>
    );
  },
);

DataTableShell.displayName = "DataTableShell";
